/**
 * 비밀번호 찾기 페이지 — Clipflow v3.6 Glass + Neon
 * 이메일 입력 후 재설정 링크 발송
 */
'use client'

import { useState, type FormEvent } from 'react'
import Link from 'next/link'
import { ClipflowLogo } from '@/components/brand/ClipflowLogo'
import { V3Icon } from '@/components/icons/V3Icon'
import { post } from '@/lib/api'

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSent, setIsSent] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)
    try {
      await post('/auth/forgot-password', { email })
      setIsSent(true)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '요청 처리 중 오류가 발생했습니다.'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <main
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 16px',
        background: 'var(--v36-bg)',
        backgroundImage: 'var(--v36-noise)',
      }}
    >
      <div className="v36-fade-in" style={{ width: '100%', maxWidth: 440 }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 28 }}>
          <Link href="/" aria-label="Clipflow 홈">
            <ClipflowLogo height={32} priority />
          </Link>
        </div>

        <div
          className="v36-glass-strong"
          style={{
            padding: 32,
            borderRadius: 18,
            boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
          }}
        >
          {isSent ? (
            <div style={{ textAlign: 'center', padding: '12px 0' }}>
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  background: 'rgba(34,217,130,0.15)',
                  border: '1px solid rgba(34,217,130,0.30)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--v36-success)',
                  marginBottom: 18,
                }}
              >
                <V3Icon name="check" size={28} />
              </div>
              <h1 className="v36-h2" style={{ margin: 0, marginBottom: 10 }}>
                메일을 발송했습니다
              </h1>
              <p
                style={{
                  fontSize: 14,
                  color: 'var(--v36-ink-3)',
                  margin: '0 auto 20px',
                  maxWidth: 320,
                  lineHeight: 1.5,
                }}
              >
                <strong style={{ color: 'var(--v36-ink)' }}>{email}</strong>로 비밀번호 재설정 링크를 보냈어요.
                메일함을 확인해주세요.
              </p>
              <Link
                href="/login"
                className="v36-btn v36-btn--neon v36-btn--lg"
                style={{ display: 'inline-flex' }}
              >
                <V3Icon name="arrowL" size={14} />
                <span>로그인으로 돌아가기</span>
              </Link>
            </div>
          ) : (
            <>
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <h1 className="v36-h2" style={{ margin: 0, marginBottom: 6 }}>
                  비밀번호 찾기
                </h1>
                <p style={{ fontSize: 13, color: 'var(--v36-ink-3)', margin: 0 }}>
                  가입하신 이메일을 입력하시면 재설정 링크를 보내드려요
                </p>
              </div>

              <form
                onSubmit={handleSubmit}
                style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
              >
                {error && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '10px 12px',
                      borderRadius: 10,
                      background: 'rgba(255,77,79,0.10)',
                      border: '1px solid rgba(255,77,79,0.30)',
                      fontSize: 13,
                      color: 'var(--v36-error)',
                    }}
                  >
                    <V3Icon name="bell" size={14} />
                    <span>{error}</span>
                  </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label
                    style={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: 'var(--v36-ink-2)',
                    }}
                  >
                    이메일
                  </label>
                  <input
                    type="email"
                    placeholder="name@example.com"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="v36-input"
                  />
                </div>

                <button
                  type="submit"
                  className="v36-btn v36-btn--neon v36-btn--lg"
                  disabled={isLoading || !email}
                  style={{
                    width: '100%',
                    marginTop: 4,
                    opacity: isLoading || !email ? 0.5 : 1,
                  }}
                >
                  {isLoading ? (
                    <>
                      <span
                        style={{
                          width: 14,
                          height: 14,
                          border: '2px solid rgba(255,255,255,0.3)',
                          borderTopColor: '#fff',
                          borderRadius: '50%',
                          animation: 'v36-spin 0.8s linear infinite',
                        }}
                      />
                      <span>발송 중...</span>
                    </>
                  ) : (
                    <>
                      <V3Icon name="bolt" size={14} />
                      <span>재설정 링크 발송</span>
                    </>
                  )}
                </button>
              </form>

              <div
                style={{
                  marginTop: 20,
                  paddingTop: 18,
                  borderTop: '1px solid var(--v36-line)',
                  textAlign: 'center',
                }}
              >
                <Link
                  href="/login"
                  style={{
                    fontSize: 13,
                    color: 'var(--v36-ink-3)',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <V3Icon name="arrowL" size={12} />
                  <span>로그인으로 돌아가기</span>
                </Link>
              </div>
            </>
          )}
        </div>

        <p
          className="v36-mono"
          style={{
            marginTop: 24,
            textAlign: 'center',
            fontSize: 10,
            color: 'var(--v36-ink-5)',
            letterSpacing: '0.10em',
          }}
        >
          © {new Date().getFullYear()} CLIPFLOW
        </p>
      </div>
    </main>
  )
}
